import { capitalize } from '@attain-apps/string-utils'
import { TypeAsserter } from '@attain-apps/type-guards'

import * as _actions from '../actions'

import * as _config from './config'
import type { Entry } from './entry'
import type { State } from './state'

export type ActionsConfig<
  TPropertiesConfig extends _config.PropertiesConfig.Constraint,
> = {
  /**
   * Sets the value of a property
   */
  [TProp in keyof TPropertiesConfig as `set${Capitalize<Extract<TProp, string>>}`]: TPropertiesConfig[TProp] extends _config.PropertyConfig.Constraint<
    infer TIn,
    any,
    infer TStored
  >
    ? SetActionConfig<State<TPropertiesConfig>, TIn, TStored>
    : TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint<
          infer TKey,
          infer TIn,
          any,
          infer TStored
        >
      ? SetActionConfig<
          State<TPropertiesConfig>,
          Record<TKey, TIn>,
          Record<TKey, TStored>
        >
      : never
} & {
  /**
   * Sets the value for a particular key in a lookup property
   */
  [TProp in keyof TPropertiesConfig as TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint
    ? `putIn${Capitalize<Extract<TProp, string>>}`
    : never]: TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint<
    infer TKey,
    infer TIn,
    any,
    infer TStored
  >
    ? PutInActionConfig<
        State<TPropertiesConfig>,
        TPropertiesConfig[TProp],
        TKey,
        TIn,
        TStored
      >
    : never
} & {
  /**
   * Writes a single key-value pair into the storage
   *
   * This is used to relay the write requests to the underlying storage
   */
  writeProperty: _actions.Config.RequestOp<
    // TODO: maybe make the key/value typed properly
    State<TPropertiesConfig>,
    { entry: Entry } & OnlyUpdateStateOption,
    { entry: Entry } & Required<OnlyUpdateStateOption>,
    undefined,
    undefined
  >
  /**
   * Writes the entire set of values into the storage
   */
  writeAllProperties: _actions.Config.RequestOp<
    State<TPropertiesConfig>,
    { values: State.Values<TPropertiesConfig> } & OnlyUpdateStateOption,
    {
      values: State.Values<TPropertiesConfig>
    } & Required<OnlyUpdateStateOption>,
    undefined,
    undefined
  >
}

export function getActionsConfig<
  TPropertiesConfig extends _config.PropertiesConfig.Constraint,
>(config: TPropertiesConfig): ActionsConfig<TPropertiesConfig> {
  // Since properties can have any name, and selector names are based on
  // property names, we end up with just a record of selectors or selector
  // methods
  const actions: Record<string, _actions.Config.RequestOp<any>> = {
    writeAllProperties: _actions.request({
      input: ({ values, onlyUpdateState = false }) => ({
        onlyUpdateState,
        values,
      }),
      output: _actions.Data.NONE,
      reducersDeprecated: {
        success: (state, { request: { values } }) => ({
          ...state,
          values,
        }),
      },
    }),
    writeProperty: _actions.request({
      input: ({ entry, onlyUpdateState = false }) => ({
        entry,
        onlyUpdateState,
      }),
      output: _actions.Data.NONE,
      reducersDeprecated: {
        success: (state, { request: { entry } }) => {
          const values = { ...state.values }
          if (entry.value === undefined) {
            // If the value is `undefined`, we can just remove it from the state
            delete values[entry.key]
          } else {
            // NOTE: TypeScript does not infer the proper values when using the
            // properties config constraint. This may not be the best solution,
            // but couldn't think of a better one
            values[entry.key] = entry.value as never
          }
          return { ...state, values }
        },
      },
    }),
  } satisfies ActionsConfig<_config.PropertiesConfig.Constraint>

  for (const prop in config) {
    const propConfig = config[prop]
    const setActionName = `set${capitalize(prop)}` as const

    switch (propConfig[_config.Marker.SYMBOL]) {
      case _config.Marker.Type.Property: {
        const { set } = propConfig
        // Set action for the property
        actions[setActionName] = _actions.request({
          input: set
            ? input => current => set(input, current)
            : input => () => input,
          output: _actions.Data.NONE,
        }) satisfies SetActionConfig<State<TPropertiesConfig>, unknown, unknown>
        break
      }
      case _config.Marker.Type.Lookup: {
        const { set } = propConfig
        // Set action for the lookup itself
        actions[setActionName] = _actions.request({
          input: set
            ? input => current => {
                const result: Record<string, unknown> = {}
                for (const key in input) {
                  const value = (result[key] = set(
                    key,
                    input[key],
                    current?.[key],
                  ))
                  if (value === undefined) {
                    // If the value is `undefined`, we might as well just remove
                    // it from the state altogether
                    delete result[key]
                  }
                }
                return result
              }
            : input => () => input,
          output: _actions.Data.NONE,
        }) satisfies SetActionConfig<
          State<TPropertiesConfig>,
          Record<string, unknown>,
          Record<string, unknown>
        >
        // Put in action for the lookup
        actions[`putIn${capitalize(prop)}`] = _actions.request({
          input: input => currentLookup => {
            const newLookup = { ...currentLookup }
            const key = input.key ?? propConfig.defaultKey
            const value = set
              ? set(key, input.value, currentLookup?.[key])
              : input.value
            if (value === undefined) {
              // If the value is `undefined`, we might as well just remove it
              // from the state altogether
              delete newLookup[key]
            } else {
              newLookup[key] = value
            }
            // Returns the new lookup
            return newLookup
          },
          output: _actions.Data.NONE,
        }) satisfies PutInActionConfig<
          State<TPropertiesConfig>,
          _config.LookupConfig.Constraint,
          string,
          unknown,
          unknown
        >
        break
      }
      default:
        throw TypeAsserter.assertIsNever(propConfig[_config.Marker.SYMBOL])
    }
  }
  // NOTE: since we don't know what properties are going to be given, and the
  // return type is entirely based on the properties that are given, we couldn't
  // find a way to get TypeScript to understand the
  return actions as ActionsConfig<TPropertiesConfig>
}

interface OnlyUpdateStateOption {
  /**
   * By default, this action is used to both update the in-memory state, as well
   * as write to whatever underlying storage is being used. If this is set to
   * `true`, only the state is updated, nothing is written to the underlying
   * storage.
   *
   * This is generally only used interally by the storage to write external
   * updates to the underlying storage into the in-memory state.
   *
   * @default false
   */
  onlyUpdateState?: boolean
}

type SetActionConfig<TState, TIn, TStored> = _actions.Config.RequestOp<
  TState,
  TIn,
  // Uses the current value to get the new value, in case a setter is used
  (current: TStored | undefined) => TStored,
  undefined,
  undefined
>

/**
 * Accepts a single {@link Entry} for the input, but ultimately produces a
 * request of the same structure as {@link SetActionConfig} so that they can be
 * handled by the same effect
 */
type PutInActionConfig<
  TState,
  TPropConfig extends _config.LookupConfig.Constraint,
  TKey extends string,
  TIn,
  TStored,
> = SetActionConfig<
  TState,
  // Key can be omitted if we have a partial key
  undefined extends TPropConfig['defaultKey']
    ? Entry<TKey, TIn>
    : Partial.Semi<Entry<TKey, TIn>, 'key'>,
  // We end up with the full lookup
  Record<TKey, TStored>
>
